<template>
  <Screen>
    <AppBar
      title="Manage users"
      :description="`Manage all BIBO app users by adding or editing their details`"
      :showBackButton="false"
    >
      <inertia-link
        :href="$route('admin.users.create')"
        class="btn btn-sm btn-primary"
      >
        <i class="fa fa-plus"></i>
        <span class="d-none d-app-inline">Add new</span>
      </inertia-link>
    </AppBar>
    <Breadcrumbs title="Users" />
    <div class="card">
      <div class="card-body">
        <div class="row">
          <div class="col-lg-8 order-1 order-lg-0">
            <SearchBar
              @onSubmit="searchSubmit"
              @onClear="searchCleared"
              v-model="searchInput"
            />
          </div>
          <div
            class="
              col-lg-4
              mb-3 mb-lg-0
              text-left text-lg-right
              order-0 order-lg-1
            "
          >
            <inertia-link
              :href="$route('admin.users.create')"
              class="btn btn-sm btn-primary"
            >
              Add new user
              <i class="fa fa-plus ml-1"></i>
            </inertia-link>
            <a
              :href="$route('admin.users.export.csv')"
              class="btn btn-sm btn-success"
              download
            >
              Export users
              <i class="fa fa-file-excel ml-1"></i>
            </a>
          </div>
        </div>
        <div class="filters mb-3">
          <Dropdown
            header="Select user status"
            :items="filtersStatus"
            @selected="filterStatusChanged"
          />
          <Dropdown
            header="Select payment status"
            :items="filtersPayment"
            @selected="filterPaymentChanged"
          />
          <Dropdown
            header="Select user role"
            :items="filtersRole"
            @selected="filterRoleChanged"
          />
          <Dropdown
            header="<small class='fa fa-calendar mr-2'></small>Select last used"
            :items="filtersLastUsed"
            @selected="filterLastUsedChanged"
          />
          <Dropdown
            header="<small class='fa fa-stopwatch mr-2'></small>Select streak order"
            :items="filtersStreak"
            @selected="filterStreakChanged"
          />
          <inertia-link
            :href="$route('admin.users.index')"
            class="btn btn-danger btn-sm"
            v-if="hasFilters"
            replace
          >
            <i class="fa fa-times mr-1"></i> Clear all filters
          </inertia-link>
        </div>
        <template v-if="data.data.length > 0">
          <div class="table-responsive">
            <table class="table mb-0">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Role</th>
                  <th>Streak</th>
                  <th>Status</th>
                  <th>Payment status</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in data.data" :key="item.id">
                  <td>
                    {{
                      i + 1 + (data.meta.current_page - 1) * data.meta.per_page
                    }}
                  </td>
                  <th>{{ item.name }}</th>
                  <td>{{ item.email }}</td>
                  <td>{{ item.mobile }}</td>
                  <td>
                    <span
                      class="badge badge-pill"
                      :class="{
                        'badge-warning': item.role == 'admin',
                        'badge-primary': item.role == 'user',
                      }"
                      v-text="item.role"
                    ></span>
                  </td>
                  <th>
                    <template v-if="item.streak_count > 0">
                      <div v-text="item.streak_count"></div>
                      <div class="sub-title">
                        <b>{{ item.trees_planted }}</b> trees
                      </div>
                    </template>
                    <span v-else class="streak-count text-dim">0</span>
                  </th>
                  <th>
                    <StatusSwitch
                      :dataid="item.id"
                      :status="item.isactive"
                      action="userStatus"
                    />
                  </th>
                  <th>
                    <StatusSwitch
                      :dataid="item.id"
                      :status="item.payment_done"
                      labelText="Paid/Unpaid"
                      action="userPaymentStatus"
                    />
                  </th>
                  <td>{{ item.created_at_human || "-" }}</td>
                  <td>{{ item.last_used_human || "-" }}</td>
                  <td>
                    <inertia-link
                      class="btn btn-sm btn-light"
                      :href="
                        $route('admin.users.details', {
                          id: item.id,
                        })
                      "
                    >
                      Details
                    </inertia-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Pagination :rawData="data.meta" :onClick="pageChange" />
        </template>
        <NoData title="No users added yet" v-else>
          <inertia-link
            :href="$route('admin.users.create')"
            class="btn btn-primary"
            ><i class="fa fa-plus"></i> Add new</inertia-link
          >
        </NoData>
      </div>
    </div>
  </Screen>
</template>
<script>
import Dropdown from "@/components/dropdown";
export default {
  components: {
    Dropdown,
  },
  props: {
    data: {
      type: [Array, Object],
      default: () => [],
    },
    search: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    lastUsedFilters: {
      type: Array,
      default: () => [],
    },
    roleFilters: {
      type: Array,
      default: () => [],
    },
    streakFilters: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    "filters.search": {
      immediate: true,
      handler(value) {
        this.searchInput = value;
      },
    },
  },
  data() {
    return {
      searchInput: "",
    };
  },
  mounted() {},
  methods: {
    searchSubmit(search) {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          search: search,
          page: 1,
        })
      );
    },
    searchCleared() {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          search: "",
          page: 1,
        })
      );
    },
    pageChange(page) {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          page: page,
        })
      );
    },
    filterStatusChanged(item) {
      if (this.filters.status === item.id) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          status: item.id,
          page: 1,
        })
      );
    },
    filterPaymentChanged(item) {
      if (this.filters.payment === item.id) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          payment: item.id,
          page: 1,
        })
      );
    },
    filterRoleChanged(item) {
      if (this.filters.role === item.id) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          role: item.id,
          page: 1,
        })
      );
    },
    filterLastUsedChanged(item) {
      if (this.filters.last_used === item.id) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          last_used: item.id,
          page: 1,
        })
      );
    },
    filterStreakChanged(item) {
      if (this.filters.streak === item.id) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          streak: item.id,
          page: 1,
        })
      );
    },
  },
  computed: {
    filtersStatus() {
      return [
        {
          id: "all",
          title:
            '<small class="fa fa-minus-circle mr-2"></small>Display all status',
          active: this.filters.status == "all",
        },
        {
          id: 0,
          title:
            '<small class="fa fa-exclamation-circle text-warning mr-2"></small>In-active',
          active: this.filters.status == 0,
        },
        {
          id: 1,
          title:
            '<small class="fa fa-check-circle text-success mr-2"></small>Active',
          active: this.filters.status == 1,
        },
      ];
    },
    filtersPayment() {
      return [
        {
          id: "all",
          title:
            '<small class="fa fa-minus-circle mr-2"></small>Display all payment status',
          active: this.filters.payment == "all",
        },
        {
          id: 0,
          title:
            '<small class="fa fa-exclamation-circle text-warning mr-2"></small>Unpaid',
          active: this.filters.payment == 0,
        },
        {
          id: 1,
          title:
            '<small class="fa fa-check-circle text-success mr-2"></small>Paid',
          active: this.filters.payment == 1,
        },
      ];
    },
    filtersRole() {
      let roles = [...this.roleFilters];

      roles.unshift({
        id: "all",
        title:
          '<small class="fa fa-minus-circle mr-2"></small>Display all roles',
      });

      return roles.map((item) => {
        return {
          id: item.id,
          title: item.title,
          active: this.filters.role == item.id,
        };
      });
    },
    filtersLastUsed() {
      return this.lastUsedFilters.map((item) => {
        return {
          id: item.id,
          title: item.title,
          active: this.filters.last_used == item.id,
        };
      });
    },
    filtersStreak() {
      return this.streakFilters.map((item) => {
        return {
          id: item.id,
          title: item.title,
          active: this.filters.streak == item.id,
        };
      });
    },
    hasFilters() {
      return (
        (this.filters.status && this.filters.status !== "all") ||
        (this.filters.payment && this.filters.payment !== "all") ||
        (this.filters.role && this.filters.role !== "all") ||
        (this.filters.last_used && this.filters.last_used !== "all") ||
        (this.filters.streak && this.filters.streak !== "all")
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.streak-count.text-dim {
  opacity: 0.4;
  font-weight: 400;
}
</style>
